// @flow
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import cx from 'classnames';
import _ from 'shared/lib/gettext';
import Button from '~/app/ui/Button';
import AssetPreview from './AssetPreview';
import { S, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  selectedAsset: ?string,
  previewAssets: ?string[],
  onOpenAssetBrowser: () => void,
  onUpdateAsset: (string) => void,
  options: object,
}

const FilePicker = (props: Props) => {
  const {
    classes: c, selectedAsset, previewAssets, onOpenAssetBrowser, onUpdateAsset, options
  } = props;

  const [editing, setEditing] = useState<boolean>(false);
  const [editedAsset, setEditedAsset] = useState<string>('');

  const onSave = () => {
    setEditing(false);
    onUpdateAsset(editedAsset);
  };
  const onEdit = () => {
    setEditedAsset(selectedAsset);
    setEditing(true);
  };
  const onCancel = () => setEditing(false);

  const validationPattern = new RegExp(options?.validation?.pattern, 'i');
  const testValidation = validationPattern.test(editedAsset);

  const validationMessage = options?.validation?.message ?? ''; 
  const translateValidationMessage = validationMessage ? _(validationMessage) : '';

  return (
    <React.Fragment>
      <div className={c.container}>
        <div className={c.filenameContainer}>
          <textarea
            className={c.filename}
            aria-label="Asset address"
            disabled={!editing}
            value={editing ? editedAsset : selectedAsset}
            onChange={(e) => { setEditedAsset(e.target.value); }}
          />
          {/* Real buttons with names, so keyboards, screen readers and AI assistants can use them. */}
          {
            editing ? (
              <React.Fragment>
                <button type="button" className={cx('material-icons', c.icon)} onClick={onSave} aria-label="Save address" title="Save address">save</button>
                <button type="button" className={cx('material-icons', c.icon)} onClick={onCancel} aria-label="Cancel editing the address" title="Cancel">close</button>
              </React.Fragment>
            ) : <button type="button" className={cx('material-icons', c.icon)} onClick={onEdit} aria-label="Edit address" title="Type or paste an address">edit</button>
          }
        </div>
        {
          (testValidation && translateValidationMessage) && (
            <label className={c.validationMessage}>{translateValidationMessage}</label>
          )
        }
        { !editing && <Button size="sm" icon="photo_library" className={c.button} onClick={onOpenAssetBrowser}>{_('SELECT_ASSET')}</Button> }
      </div>
      {
        previewAssets?.length ? (
          <div className={c.previewAssetsContainer}>
            { previewAssets.map(([key, asset]) => (
              <div key={asset} className={cx(c.previewAssetsItem, { [c.previewAssetsItemLarge]: key !== 'mobile' })}>
                <AssetPreview asset={asset} shouldPoll />
                <div className={c.previewAssetsCaption}>{key}</div>
              </div>
            ))
          }
          </div>
        ) : <AssetPreview asset={selectedAsset} />
      }
    </React.Fragment>
  );
};

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    margin: '16px 0px',
    flexDirection: 'column'
  },
  filenameContainer: {
    maxWidth: '500px',
    display: 'flex',
    alignItems: 'center'
  },
  filename: {
    flexGrow: '1',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    borderRadius: '3px',
    fontSize: '13px',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '400',
    outline: 'none',
    wordBreak: 'break-all',
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: 'hsl(0, 0%, 90%)',
      color: 'hsl(0, 0%, 50%)'
    },
    [DARK]: {
      borderColor: S.fieldLine,
      backgroundColor: S.field,
      color: S.fg,
      '&:disabled': { backgroundColor: S.chromeHover, color: S.muted }
    }
  },
  icon: {
    margin: '0px 4px',
    padding: 2,
    border: 0,
    borderRadius: 4,
    background: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:focus-visible': { outline: `2px solid ${S.fg}`, outlineOffset: 1 }
  },
  button: {
    alignSelf: 'flex-start',
    marginTop: 10
  },
  previewAssetsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& > *': {
      flex: '1 1 0px'
    }
  },
  previewAssetsItem: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:first-child': {
      width: 'auto',
      flexGrow: 1,
    },
    justifyContent: 'center',
  },
  previewAssetsItemLarge: {
    flexGrow: 2,
    '&> *:first-child': {
      borderLeft: '1px solid #bbb'
    },
    [DARK]: { '&> *:first-child': { borderLeftColor: S.line } }
  },
  previewAssetsCaption: {
    textAlign: 'center',
    fontSize: '12px'
  },
  validationMessage: {
    color: '#858585',
    [DARK]: { color: S.muted },
    fontSize: '13px',
    marginLeft: '4px',
    marginTop: '10px',
  },
};

export default injectSheet(styles)(FilePicker);
